import { Title, Divider, ActionGroup, Flex } from '@patternfly/react-core'
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import { useCallback, useState, useMemo } from 'react'
import * as React from 'react'

import type {
  PatientMedication,
  MedicationRoute,
  MedicationDosageUnit,
} from '@app/domain/patient-medication-history/patient-medication-history.interface'
import { ActionLink } from '@app/utils/styles/action-link'
import { SmallButton } from '@app/utils/styles/small-button'

import { EditableMedicationForm } from '../editable-medication-form'

interface Props {
  editMedication: (
    patientMedicationId: string,
    medicationDosageUnitId: string,
    medicationRouteId: string,
    name: string,
  ) => void
  deleteMedication: (patientMedicationId: string) => void
  patientMedication: PatientMedication
  medicationRoutes: MedicationRoute[]
  medicationDosageUnits: MedicationDosageUnit[]
}

export const EditMedicationPopOverContent = ({
  editMedication,
  deleteMedication,
  patientMedication,
  medicationRoutes,
  medicationDosageUnits,
}: Props) => {
  const [showEditContent, setShowEditContent] = useState(false)
  const [showRemoveWarningContent, setShowRemoveWarningContent] = useState(
    false,
  )

  const handleShowEditContent = useCallback(() => {
    setShowEditContent(true)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setShowEditContent(false)
  }, [])

  const handleExitFromWarning = useCallback(() => {
    setShowRemoveWarningContent(false)
  }, [])

  const handleShowRemoveWarning = useCallback(() => {
    setShowRemoveWarningContent(true)
  }, [])

  const submitRemoveMedication = useCallback(() => {
    deleteMedication(patientMedication.id)
    setShowRemoveWarningContent(false)
  }, [deleteMedication, patientMedication.id])

  const handleSubmit = useCallback(
    (
      patientMedicationId: string,
      medicationDosageUnitId: string,
      medicationRouteId: string,
      name: string,
    ) => {
      setShowEditContent(false)
      editMedication(
        patientMedicationId,
        medicationDosageUnitId,
        medicationRouteId,
        name,
      )
    },
    [editMedication],
  )

  const popOverContent = useMemo(() => {
    if (showEditContent) {
      return (
        <>
          <Title headingLevel="h4">Edit Medication</Title>
          <EditableMedicationForm
            onEditMedication={handleSubmit}
            onCancel={handleCancelEdit}
            medicationRoutes={medicationRoutes}
            medicationDosageUnits={medicationDosageUnits}
            patientMedication={patientMedication}
          />
        </>
      )
    } else if (showRemoveWarningContent) {
      return (
        <>
          <Title headingLevel="h3" style={{ color: 'red' }}>
            <ExclamationCircleIcon /> Danger
          </Title>

          <p style={{ maxWidth: '250px', wordWrap: 'break-word' }}>
            Removing a medication will cause each of its associated entries to
            be removed from all records.
          </p>
          <ActionGroup style={{ marginLeft: 'auto', marginTop: 10 }}>
            <Flex>
              <SmallButton
                style={{ marginRight: 5 }}
                key="cancel"
                variant="secondary"
                onClick={handleExitFromWarning}
              >
                Cancel
              </SmallButton>
              <SmallButton
                key="confirm"
                variant="danger"
                onClick={submitRemoveMedication}
              >
                Continue
              </SmallButton>
            </Flex>
          </ActionGroup>
        </>
      )
    } else {
      return (
        <>
          <div>
            <ActionLink onClick={handleShowEditContent}>Edit</ActionLink>

            <Divider />
            <ActionLink
              style={{ color: 'red' }}
              onClick={handleShowRemoveWarning}
            >
              Remove
            </ActionLink>
          </div>
        </>
      )
    }
  }, [
    handleCancelEdit,
    handleExitFromWarning,
    handleShowEditContent,
    handleShowRemoveWarning,
    handleSubmit,
    medicationDosageUnits,
    medicationRoutes,
    patientMedication,
    showEditContent,
    showRemoveWarningContent,
    submitRemoveMedication,
  ])

  return popOverContent
}
